import {
  rowAccounts,
  type OpenerDescalcePlatform,
  type OpenerDescalceRow
} from '../../api/useOpenerDescalce';

const VENUE_LABEL: Record<OpenerDescalcePlatform, string> = {
  kalshi: 'Kalshi',
  polymarket: 'Polymarket'
};

/**
 * The legs the sweep did NOT reach, in wire order. A leg is treated as unread
 * on `legsRead[leg] === false` and on a MISSING `legsRead` block: a payload
 * that cannot say which legs it read has not established that it read either,
 * and the safe reading of "unknown" is never "flat".
 */
export function unreadLegs(row: OpenerDescalceRow): OpenerDescalcePlatform[] {
  const legs = row.legsRead;
  return (['kalshi', 'polymarket'] as const).filter(
    leg => legs?.[leg] !== true
  );
}

export function hasUnreadLeg(row: OpenerDescalceRow): boolean {
  return unreadLegs(row).length > 0;
}

/**
 * One venue's per-side contract count, gated on that leg having been READ.
 * Checked against `legsRead` and not only against the value, so a payload
 * carrying the previous sweep's numbers next to `legsRead[leg] = false` still
 * renders the unknown — four concrete counts that add up to a hedged position
 * are read as one, whatever the derived columns say.
 */
export function legCount(
  row: OpenerDescalceRow,
  leg: OpenerDescalcePlatform,
  value: number | null | undefined
): number | null {
  if (row.legsRead?.[leg] !== true) return null;
  return value ?? null;
}

/**
 * Why a cross-venue number is `—` on this row. Names the account too: on a
 * fleet of several accounts per venue, "Kalshi was not read" is not actionable
 * until the operator knows WHICH Kalshi account.
 */
export function unreadLegTitle(row: OpenerDescalceRow): string {
  const unread = unreadLegs(row);
  if (unread.length === 0) return '';
  const accounts = rowAccounts(row);
  const named = unread
    .map(leg =>
      leg === 'kalshi'
        ? `${VENUE_LABEL.kalshi} (${accounts.kalshiAccountId})`
        : `${VENUE_LABEL.polymarket} (${accounts.polymarketAccountId})`
    )
    .join(' and ');
  return `NOT READ: ${named}. This row's position is unknown, which is not the same as flat — no cross-venue descalce can be computed from it.`;
}
